import { Injectable } from '@angular/core';
import { Store } from '../../../services/store';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { BomTreeProvider } from './bom-tree.provider';
import { toVoid } from '../../../utils/void';
import { parseAmount } from '../../../utils/format-amount';
import { addChild, BomTreeItemData, BomTreeItemVm, BomTreeVm, mapModelToViewModel, removeNode, replaceNode, updateNode, updateNodesByBomItemId } from './models/bom-tree-item.view-model';
import { guid } from '../../../utils/guid';
import { Point } from '../../../models/point';
import { BomItemSelected } from '../../add-node-dropdown/add-node-dropdown.component';
import { CreateBomItemRequestDto } from './dtos/create-bom-item-request.dto';
import { PatchBomItemRequestDto } from './dtos/patch-bom-item-request.dto';
import { BomStructure } from '../../../models/bom-structure';

// the WYBRANY ELEMENT card's editable fields — the name is editable there too, same PATCH as the tree
export type BomItemAttribute = 'name' | 'partNumber' | 'structure' | 'unit' | 'massKg' | 'description';

export interface BomTreeState {
  loading: boolean;
  root: BomTreeItemVm | undefined;
  rootBomItemId: number | 'new' | undefined;
  selectedNodeId: string | undefined; // VM node id, not bomItemId — unsaved nodes are selectable too
  addNodeDropdown: {
    triggerNodeId: string;
    position: Point;
  } | undefined;
  contextMenu: {
    nodeId: string;
    position: Point;
  } | undefined;
  // one card field is edited at a time (the overseer headerEdit precedent); the tree node's own
  // name and amount editors live in the node component (0124)
  attributeEdit: {
    field: BomItemAttribute;
  } | undefined;
  attributeEditError: string | undefined;
  // routingGraph: BomRoutingGraph | undefined;
  // routing: BomRouting | undefined;
}

export interface BomRouting {
  nests: ProductionNest[];
  steps: OperationSequenceStep[];
}

export interface BomRoutingRow {
  nest: string; // todo: add id, name etc
  steps: OperationSequenceStep[]; // op 10, op 20, op 30 etc...
}

export interface BomRoutingLevel {
  number: number;

}

export interface ProductionNest {
  id: number;
  name: string;
}

export interface OperationSequenceStep {
  number: number;
  inputs: RoutingBomItem[];
  output: RoutingBomItem;
}

export interface RoutingBomItem {
  id: number;
  name: string;
  amount: number;
}

// TODO: item or node ???
export interface BomRoutingGraphItem {
  data: BomTreeItemData;
}

const INITIAL_STATE: BomTreeState = {
  loading: false,
  root: undefined,
  rootBomItemId: undefined,
  selectedNodeId: undefined,
  addNodeDropdown: undefined,
  contextMenu: undefined,
  attributeEdit: undefined,
  attributeEditError: undefined,
  // routing: undefined
  // routingGraph: undefined,
};

export interface UpdateRecord {
  type: string;
  payload: unknown;
}

export interface CreateItemRecord extends UpdateRecord {
  type: 'create_item';
  payload: {
    name: string;
  }
}

@Injectable({ providedIn: 'root' })
export class BomTreeStore extends Store<BomTreeState> {
  constructor(private provider: BomTreeProvider) {
    super(INITIAL_STATE);
  }

  init(id: number | 'new'): Observable<void> {
    if (id === 'new') {
      this.update(state => {
        const root = mapModelToViewModel({
          amount: 1,
          children: [],
          name: 'Nowy produkt',
          type: 'maszyna',
          structure: 'Normal',
          unit: 'szt',
          bomItemId: undefined,
          bomRelationId: undefined,
          workflow: undefined,
        });

        return {
          ...state,
          root,
          rootBomItemId: id,
          selectedNodeId: root.id,
          routing: {
            steps: [10, 20, 30, 40, 50].map(x => ({ number: x })),
            nests: [
              {
                id: 1,
                name: 'Gięcie'
              },
              {
                id: 2,
                name: 'Cięcie'
              }
            ]
          }
        }
      });

      return of(undefined);
    }

    this.update(state => ({ ...state, loading: true }));

    return this.provider.getBomTree(id)
      .pipe(
        map(mapModelToViewModel),
        tap(root => {
          this.update(state => ({
            ...state,
            root,
            rootBomItemId: id,
            selectedNodeId: root.id,
            loading: false
          }));
        }),
        toVoid(),
      );
  }

  selectNode(nodeId: string): void {
    // selecting a node that no longer exists (removed mid-interaction) is a no-op
    if (!this.state.root || !BomTreeVm.findNodeById(this.state.root, nodeId)) {
      return;
    }

    this.update(state => ({ ...state, selectedNodeId: nodeId }));
  }

  /**
   * The tree's name editor (0124). A blank name is a no-op. An unsaved node — the /bom/new root — is
   * created by its first name; a saved one is patched, and every node of that item follows, as after
   * the card's saveAttribute('name'). The tree changes only once the API accepted the name.
   */
  renameNode(nodeId: string, value: string): Observable<void> {
    const name = value.trim();
    const node = BomTreeVm.getNodeById(this.state.root!, nodeId);

    if (!name) {
      return of(undefined);
    }

    const bomItemId = node.data.bomItemId;

    if (!bomItemId) {
      return this.createItem(nodeId, name);
    }

    return this.provider.patchBomItem(bomItemId, { name })
      .pipe(
        tap(() => {
          this.updateTree(updateNodesByBomItemId(bomItemId, node => ({
            ...node,
            data: {
              ...node.data,
              name
            }
          })));
        })
      );
  }

  /**
   * The tree's amount editor (0124): the quantity on the relation leading to this one node, so only
   * this node changes (the same item elsewhere has its own relation). Anything not above zero is a
   * no-op (the ClientOrderStore.updateAmount rule), as is a node without a relation to patch.
   */
  setNodeAmount(nodeId: string, value: string): Observable<void> {
    const amount = parseAmount(value);
    const { bomItemId, bomRelationId } = BomTreeVm.getNodeById(this.state.root!, nodeId).data;

    if (!(amount > 0) || !bomItemId || !bomRelationId) {
      return of(undefined);
    }

    return this.provider.patchBomItem(bomItemId, { bomRelationId, amount })
      .pipe(
        tap(() => {
          this.updateTree(updateNode(nodeId, node => ({
            ...node,
            data: {
              ...node.data,
              amount
            }
          })));
        })
      );
  }

  private createItem(nodeId: string, name: string): Observable<void> {
    // TODO: add and set 'saving/loading' flag for node

    const { node, parent } = BomTreeVm.getNodeWithParentById(this.state.root!, nodeId);

    if (!node) {
      throw new Error(`Cannot create BOM item. Node not found: ${nodeId}`);
    }

    const request: CreateBomItemRequestDto = {
      name,
      parentBomItemId: parent?.data?.bomItemId,
      amount: node.data.amount
    };

    return this.provider.createBomItem(request)
      .pipe(
        tap(({ bomItemId }) => {
          if (!!node.data.bomItemId) {
            return;
          }

          this.updateTree(updateNode(nodeId, node => ({
            ...node,
            data: {
              ...node.data,
              bomItemId,
              name
            }
          })),
          {
            after: [state => ({ ...state, rootBomItemId: state. rootBomItemId === 'new' ? bomItemId : state.rootBomItemId })]
          });
        }),
        toVoid()
      );
  }

  deleteItem(nodeId: string): Observable<void> {
    // TODO: add 'loading' flags etc

    const { node: removedNode, parent } = BomTreeVm.getNodeWithParentById(this.state.root!, nodeId);

    if (!removedNode) {
      throw new Error(`Node ${nodeId} not found`);
    }

    // removing the selection (or an ancestor of it) moves the selection to the removed node's parent
    const selectionRemoved = [...BomTreeVm.traverse(removedNode)].some(node => node.id === this.state.selectedNodeId);

    this.updateTree(removeNode(nodeId), {
      after: selectionRemoved && parent ? [state => ({ ...state, selectedNodeId: parent.id })] : []
    });

    if (!removedNode.data.bomItemId) {
      throw new Error('Cannot delete BOM item without id.');
    }

    // TODO: should I create an save commands queue with such updates? is it beneficial?
    return this.provider.deleteBomItem(removedNode.data.bomItemId);
  }

  openAddNodeDropdown(triggerNodeId: string, position: Point): void {
    this.update(state => ({
      ...state,
      addNodeDropdown: {
        triggerNodeId,
        position
      }
    }));
  }

  closeAddNodeDropdown(): void {
    this.update(state => ({ ...state, addNodeDropdown: undefined }));
  }

  openContextMenu(nodeId: string, position: Point): void {
    this.update(state => ({
      ...state,
      // a right-click is not a 'click', so the dropdown's clickOutside never fires — close it here
      addNodeDropdown: undefined,
      contextMenu: {
        nodeId,
        position
      }
    }));
  }

  closeContextMenu(): void {
    this.update(state => ({ ...state, contextMenu: undefined }));
  }

  // TODO: use different model than BomTreeItem here (BomItem with no children)
  addChildFromDropdownItem(newChild: BomItemSelected): Observable<void> {
    // const newChildVm = mapModelToViewModel(newChild);

    const parentNodeId = this.state.addNodeDropdown?.triggerNodeId;

    if (!parentNodeId) {
      throw new Error('Cannot add child from dropdown when dropdown is closed (cannot define parent node).');
    }

    // TODO: add flag about 'hydrating / loading / saving' here
    const newChildVm: BomTreeItemVm = {
      id: guid(),
      hydrating: !!newChild.bomItemId,
      data: {
        bomItemId: newChild.bomItemId,
        bomRelationId: undefined,
        name: newChild.name,
        type: '',
        // the API defaults for a JIT child; a hydrated one brings its own
        structure: 'Normal',
        unit: 'szt',
        amount: 1,
        workflow: undefined,
      },
      children: []
    };

    this.updateTree(addChild(parentNodeId, newChildVm));

    // Add two async commands here:
    // - save new relation
    // - hydrate added child BOM tree model (either from FE cache or by requesting BE)

    /**
     * Alternative flow:
     * - hydrate BOM tree
     * - validate BOM tree correctness (no cycles, parents inside parents etc)
     * - then enqueue save
     * 
     * ^ this way we make less BE roundtrips, because if we first save relation then we have to handle BE validation too
     */

    // I think that having a 'save queue' is good idea.
    // Should reads for hydrations also be a part of this queue?

    if (!newChild.bomItemId) {
      const parentNode = BomTreeVm.getNodeById(this.state.root!, parentNodeId);
      const request: CreateBomItemRequestDto = {
        name: newChild.name,
        parentBomItemId: parentNode.data.bomItemId,
        amount: 1
      };
      return this.provider.createBomItem(request)
        .pipe(
          tap(({ bomItemId, bomRelationId }) => {
            this.updateTree(updateNode(newChildVm.id, node => ({
              ...node,
              data: {
                ...node.data,
                bomItemId: bomItemId,
                bomRelationId: bomRelationId
              }
            })));
          }),
          toVoid()
        );
    }

    return this.provider.getBomTree(newChild.bomItemId)
      .pipe(
        // keep the temp node's id, so identity (e.g. selection) survives hydration
        map(model => ({ ...mapModelToViewModel(model), id: newChildVm.id })),
        tap(newNode => {
          this.updateTree(replaceNode(newChildVm.id, newNode));
        }),
        tap(() => {
          // validate tree correctness here = no cycles etc
        }),
        switchMap(newNode => {
          const parentNode = BomTreeVm.getNodeById(this.state.root!, parentNodeId);
          return this.provider.attachChild(newChild.bomItemId!, parentNode.data.bomItemId!)
            .pipe(
              map(response => [newNode, response.bomRelationId] as [BomTreeItemVm, number])
            );
        }),
        tap(([newNode, bomRelationId]) => {
          this.updateTree(updateNode(newNode.id, node => ({
            ...node,
            data: {
              ...node.data,
              bomRelationId
            }
          })));
        }),
        toVoid()
      );
  }

  startAttributeEdit(field: BomItemAttribute): void {
    // an unsaved node (the /bom/new root before its first Enter) has nothing to patch yet
    if (!getSelectedNode(this.state).data.bomItemId) {
      return;
    }

    this.update(state => ({ ...state, attributeEdit: { field }, attributeEditError: undefined }));
  }

  cancelAttributeEdit(): void {
    this.update(state => ({ ...state, attributeEdit: undefined, attributeEditError: undefined }));
  }

  // One PATCH per field. The API validates; its sentence shows under the field (the 0042 inline message)
  saveAttribute(field: BomItemAttribute, value: string): Observable<void> {
    const bomItemId = getSelectedNode(this.state).data.bomItemId!;
    const patch = attributePatch(field, value);

    if ('error' in patch) {
      this.update(state => ({ ...state, attributeEditError: patch.error }));
      return of(undefined);
    }

    return this.provider.patchBomItem(bomItemId, patch.request)
      .pipe(
        tap(() => {
          // the same item can sit in several places of the tree — every node of it refreshes
          this.updateTree(updateNodesByBomItemId(bomItemId, node => ({
            ...node,
            data: {
              ...node.data,
              ...patch.data
            }
          })),
          {
            after: [state => ({ ...state, attributeEdit: undefined, attributeEditError: undefined })]
          });
        }),
        catchError(error => {
          this.update(state => ({
            ...state,
            attributeEditError: typeof error.error === 'string' ? error.error : 'Nie udało się zapisać'
          }));
          return of(undefined);
        })
      );
  }

  // flags arrive with the tree; card mutations keep them live without a refetch
  // (a refetch would rebuild node ids and drop selection/in-progress edits)
  setItemHasNotes(bomItemId: number, hasNotes: boolean): void {
    this.updateTree(updateNodesByBomItemId(bomItemId, node => ({
      ...node,
      data: {
        ...node.data,
        hasNotes
      }
    })));
  }

  setItemAttachments(bomItemId: number, { hasAttachments, primaryPhotoAttachmentId }: { hasAttachments: boolean, primaryPhotoAttachmentId: number | null }): void {
    this.updateTree(updateNodesByBomItemId(bomItemId, node => ({
      ...node,
      data: {
        ...node.data,
        hasAttachments,
        primaryPhotoAttachmentId
      }
    })));
  }

  private updateTree(updateFn: (root: BomTreeItemVm) => BomTreeItemVm, options?: { after: ((state: BomTreeState) => BomTreeState)[] }): void {
    this.update(state => ({
      ...state,
      root: updateFn(state.root!)
    }),
    ...(options?.after ?? []),
    ensureSelectionResolves);
  }
}

// the card is always about the selection; the id resolves or falls back to root (ensureSelectionResolves)
export function getSelectedNode(state: BomTreeState): BomTreeItemVm {
  const root = state.root!;
  return (state.selectedNodeId && BomTreeVm.findNodeById(root, state.selectedNodeId)) || root;
}

// What one field's save sends, and what the tree stores once the API accepted it — normalised the
// same way the API does (trimmed, empty is none), so no refetch is needed
function attributePatch(field: BomItemAttribute, value: string): { request: PatchBomItemRequestDto, data: Partial<BomTreeItemData> } | { error: string } {
  const text = value.trim();

  switch (field) {
    case 'name':
      return { request: { name: text }, data: { name: text } };
    case 'partNumber':
      return { request: { partNumber: text }, data: { partNumber: text || undefined } };
    case 'description':
      return { request: { description: text }, data: { description: text || undefined } };
    case 'structure':
      return { request: { structure: text as BomStructure }, data: { structure: text as BomStructure } };
    case 'unit':
      return { request: { unit: text }, data: { unit: text } };
    case 'massKg': {
      // an empty field clears the mass — the API takes 0 as none; a Polish decimal comma is fine
      const massKg = text === '' ? 0 : parseAmount(text);

      if (Number.isNaN(massKg)) {
        return { error: 'Masa musi być liczbą.' };
      }

      return { request: { massKg }, data: { massKg: massKg || undefined } };
    }
  }
}

// invariant: one node is always selected — if a tree update orphans the selection, fall back to root
function ensureSelectionResolves(state: BomTreeState): BomTreeState {
  if (!state.root || !state.selectedNodeId) {
    return state;
  }

  const resolves = [...BomTreeVm.traverse(state.root)].some(node => node.id === state.selectedNodeId);

  return resolves ? state : { ...state, selectedNodeId: state.root.id };
}
